import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  List,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconCheck, IconUserCheck } from '@tabler/icons-react'
import { useState } from 'react'
import { ContractTermsModal } from './ContractTermsModal'

interface PackageCardProps {
  packageName: string
  packageDescription: string | null
  version: {
    id: number
    priceMonthly: string
    description: string | null | undefined
    packageVersionTasks?: Array<{
      id: number
      frequency: string | null | undefined
      maintenanceTask?: {
        id: number
        name: string
      }
    }>
  }
  isProposed?: boolean
  isCurrent?: boolean
  isLoading?: boolean
  proposalAdditionalTerms?: string | null
  onSubscribe: (versionId: number) => void
  canSubscribe?: boolean
}

export function PackageCard({
  packageName,
  packageDescription,
  version,
  isProposed = false,
  isCurrent = false,
  isLoading = false,
  proposalAdditionalTerms,
  onSubscribe,
  canSubscribe = true,
}: PackageCardProps) {
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsOpened, { open: openTerms, close: closeTerms }] =
    useDisclosure(false)

  const cardBgColor = isProposed ? 'green.0' : undefined
  const cardBorderColor = isProposed
    ? 'var(--mantine-color-green-3)'
    : undefined
  const badgeColor = isProposed ? 'green' : 'brandOrange'
  const iconColor = isProposed
    ? 'var(--mantine-color-green-6)'
    : 'var(--mantine-color-brandOrange-6)'
  const buttonColor = isProposed ? 'green' : 'brandOrange'

  return (
    <Card
      withBorder
      radius="md"
      p="lg"
      bg={cardBgColor}
      style={cardBorderColor ? { borderColor: cardBorderColor } : undefined}
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="xs">
              <Title order={3}>{packageName}</Title>
              {isProposed && (
                <Badge color="green" variant="filled">
                  Suositeltu sinulle
                </Badge>
              )}
              {isCurrent && (
                <Badge color="green" variant="light">
                  Nykyinen
                </Badge>
              )}
            </Group>
            {version.description && (
              <Text c="dimmed" mt={4}>
                {version.description}
              </Text>
            )}
          </div>
          <Badge
            size="lg"
            variant={isProposed ? 'filled' : 'light'}
            color={badgeColor}
          >
            {version.priceMonthly} €/kk
          </Badge>
        </Group>

        {isProposed && proposalAdditionalTerms && (
          <Alert
            icon={<IconUserCheck size={16} />}
            color="green"
            variant="light"
          >
            <Text
              size="sm"
              fw={500}
              style={{
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {proposalAdditionalTerms}
            </Text>
          </Alert>
        )}

        {packageDescription && <Text>{packageDescription}</Text>}

        {version.packageVersionTasks?.length && (
          <div>
            <Text fw={600} mb="xs">
              Sisältää:
            </Text>
            <List spacing="xs" icon={<IconCheck size={18} color={iconColor} />}>
              {version.packageVersionTasks.map((task) => (
                <List.Item key={task.id}>
                  <Group gap="xs">
                    <Text>{task.maintenanceTask?.name}</Text>
                    {task.frequency && (
                      <Text c="dimmed">({task.frequency})</Text>
                    )}
                  </Group>
                </List.Item>
              ))}
            </List>
          </div>
        )}

        {canSubscribe && !isCurrent && (
          <Checkbox
            checked={termsAccepted}
            onChange={(event) => setTermsAccepted(event.currentTarget.checked)}
            label={
              <Text size="sm">
                Hyväksyn{' '}
                <Anchor onClick={openTerms} size="sm">
                  sopimusehdot
                </Anchor>
              </Text>
            }
          />
        )}

        {canSubscribe ? (
          <Button
            fullWidth
            color={buttonColor}
            size={isProposed ? 'lg' : undefined}
            loading={isLoading}
            onClick={() => onSubscribe(version.id)}
            disabled={isCurrent || !termsAccepted}
          >
            {isCurrent
              ? 'Käytössä'
              : isProposed
                ? 'Aktivoi paketti'
                : 'Tilaa paketti'}
          </Button>
        ) : (
          <Text c="dimmed" ta="center" size="sm">
            Vain omistaja voi tilata paketteja
          </Text>
        )}
      </Stack>

      <ContractTermsModal opened={termsOpened} onClose={closeTerms} />
    </Card>
  )
}
